import React, { useState, useEffect } from 'react';
import { 
  HiCalendar, 
  HiTrash, 
  HiDocumentText,
  HiTag,
  HiExclamationTriangle,
  HiInformationCircle,
  HiGlobeAlt,
  HiMapPin
} from 'react-icons/hi2';
import { BaseModal } from '../../../components/Modal/BaseModal';
import { toast } from '../../../components/Toast';
import { feriadoService, paisService } from '../../../services';
import type { Feriado } from '../../../models';
import type { Pais, Estado, Cidade } from '../../../services/pais';
import { cn } from '../../../utils/formatter';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface HolidayModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  feriado?: Feriado | null;
}

const TIPO_OPTIONS = [
  { 
    value: 'NACIONAL', 
    label: 'Nacional',
    description: 'Feriados nacionais válidos em todo o país',
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800'
  },
  { 
    value: 'ESTADUAL', 
    label: 'Estadual',
    description: 'Feriados específicos do estado',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-800'
  },
  { 
    value: 'MUNICIPAL', 
    label: 'Municipal',
    description: 'Feriados específicos do município',
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    borderColor: 'border-yellow-200 dark:border-yellow-800'
  },
  { 
    value: 'FOLGA', 
    label: 'Folga',
    description: 'Dia de folga ou ponto facultativo',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-800'
  }
] as const;

export const HolidayModal: React.FC<HolidayModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  feriado
}) => {
  const [formData, setFormData] = useState<{
    data: string;
    descricao: string;
    tipo: Feriado['tipo'];
    pais_id?: number;
    estado_id?: number;
    cidade_id?: number;
  }>({
    data: '',
    descricao: '',
    tipo: 'NACIONAL'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Estados para listas
  const [paises, setPaises] = useState<Pais[]>([]);
  const [estados, setEstados] = useState<Estado[]>([]);
  const [cidades, setCidades] = useState<Cidade[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);

  const isEditMode = !!feriado;

  // Carregar listas ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      loadLists();
    }
  }, [isOpen]);

  const loadLists = async () => {
    setLoadingLists(true);
    try {
      const [paisesData, estadosData, cidadesData] = await Promise.all([
        paisService.getPaises(),
        paisService.getEstados(),
        paisService.getCidades()
      ]);
      setPaises(paisesData);
      setEstados(estadosData);
      setCidades(cidadesData);
    } catch (error) {
      console.error('Erro ao carregar listas:', error);
      toast.error('Erro ao carregar dados de localização');
    } finally {
      setLoadingLists(false);
    }
  };

  useEffect(() => {
    if (feriado) {
      const newFormData: typeof formData = {
        data: feriado.data,
        descricao: feriado.descricao,
        tipo: feriado.tipo,
      };
      
      // if (feriado.abrangencia_type?.includes('Pais') && feriado.abrangencia_id) {
      //   newFormData.pais_id = feriado.abrangencia_id;
      // } else if (feriado.abrangencia_type?.includes('Estado') && feriado.abrangencia_id) {
      //   newFormData.estado_id = feriado.abrangencia_id;
      // } else if (feriado.abrangencia_type?.includes('Cidade') && feriado.abrangencia_id) {
      //   newFormData.cidade_id = feriado.abrangencia_id;
      // }
      
      setFormData(newFormData);
    } else {
      setFormData({
        data: '',
        descricao: '',
        tipo: 'NACIONAL'
      });
    }
    setShowDeleteConfirm(false);
  }, [feriado, isOpen]);

  const handleTipoChange = (novoTipo: Feriado['tipo']) => {
    const newFormData: typeof formData = {
      data: formData.data,
      descricao: formData.descricao,
      tipo: novoTipo
      // Não incluímos pais_id, estado_id, cidade_id para resetar
    };
    setFormData(newFormData);
  };

  const handlePaisChange = (paisId: string) => {
    const newFormData: typeof formData = {
      ...formData
    };
    if (paisId) {
      newFormData.pais_id = Number(paisId);
    }
    setFormData(newFormData);
  };

  const handleEstadoChange = (estadoId: string) => {
    const newFormData: typeof formData = {
      ...formData
    };
    if (estadoId) {
      newFormData.estado_id = Number(estadoId);
    }
    setFormData(newFormData);
  };

  const handleCidadeChange = (cidadeId: string) => {
    const newFormData: typeof formData = {
      ...formData
    };
    if (cidadeId) {
      newFormData.cidade_id = Number(cidadeId);
    }
    setFormData(newFormData);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.data || !formData.descricao) {
      toast.error('Preencha todos os campos obrigatórios.');
      return;
    }

    // Validação da abrangência conforme o tipo
    if (formData.tipo === 'NACIONAL' && !formData.pais_id) {
      toast.error('Selecione um país para feriados nacionais.');
      return;
    }
    if (formData.tipo === 'ESTADUAL' && !formData.estado_id) {
      toast.error('Selecione um estado para feriados estaduais.');
      return;
    }
    if ((formData.tipo === 'MUNICIPAL' || formData.tipo === 'FOLGA') && !formData.cidade_id) {
      toast.error('Selecione uma cidade para feriados municipais/folgas.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Preparar dados com abrangencia_id baseado no tipo
      const dataToSend: any = {
        data: formData.data,
        descricao: formData.descricao,
        tipo: formData.tipo
      };

      // Adicionar o campo apropriado e abrangencia_id
      if (formData.tipo === 'NACIONAL' && formData.pais_id) {
        dataToSend.pais_id = formData.pais_id;
        dataToSend.abrangencia_id = formData.pais_id;
      } else if (formData.tipo === 'ESTADUAL' && formData.estado_id) {
        dataToSend.estado_id = formData.estado_id;
        dataToSend.abrangencia_id = formData.estado_id;
      } else if ((formData.tipo === 'MUNICIPAL' || formData.tipo === 'FOLGA') && formData.cidade_id) {
        dataToSend.cidade_id = formData.cidade_id;
        dataToSend.abrangencia_id = formData.cidade_id;
      }
      if (isEditMode && feriado) {
        await feriadoService.update(feriado.id, dataToSend);
        toast.success('Feriado atualizado com sucesso!');
      } else {
        await feriadoService.create(dataToSend);
        toast.success('Feriado criado com sucesso!');
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Erro ao salvar feriado:', error);
      toast.error(error?.response?.data?.message || error?.response?.data?.error || 'Erro ao salvar feriado.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!feriado) return;

    setIsDeleting(true);

    try {
      await feriadoService.delete(feriado.id);
      toast.success('Feriado excluído com sucesso!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Erro ao excluir feriado:', error);
      toast.error(error?.response?.data?.message || 'Erro ao excluir feriado.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // const selectedTipoInfo = TIPO_OPTIONS.find(opt => opt.value === formData.tipo);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Editar Feriado' : 'Adicionar Feriado'}
      size="lg"
      useStandardLayout
      icon={<HiCalendar className="w-8 h-8 text-blue-500" />}
      description={isEditMode ? 'Atualize as informações do feriado' : 'Cadastre um novo feriado no sistema'}
    >
      <form onSubmit={handleSubmit} className="max-h-[calc(100vh-200px)] overflow-y-auto">
        <div className="px-6 pt-6 pb-4 space-y-5">
          {/* Data */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <HiCalendar className="w-4 h-4 text-blue-500" />
              Data <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.data}
              onChange={(e) => setFormData({ ...formData, data: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              required
            />
            {formData.data && (
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <HiInformationCircle className="w-3.5 h-3.5" />
                {format(new Date(formData.data + 'T12:00:00'), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
              </p>
            )}
          </div>

          {/* Descrição */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <HiDocumentText className="w-4 h-4 text-blue-500" />
              Descrição <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.descricao}
              onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Ex: Dia do Trabalho"
              maxLength={100}
              className="w-full px-4 py-3 border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              required
            />
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              {formData.descricao.length}/100 caracteres
            </p>
          </div>

          {/* Tipo - Radio Cards */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              <HiTag className="w-4 h-4 text-blue-500" />
              Tipo <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2.5">
              {TIPO_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleTipoChange(option.value)}
                  className={cn(
                    'relative p-3 rounded-lg border-2 transition-all text-left',
                    formData.tipo === option.value
                      ? `${option.borderColor} ${option.bgColor}`
                      : 'border-gray-200 dark:border-neutral-700 hover:border-gray-300 dark:hover:border-neutral-600 bg-white dark:bg-neutral-800'
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className={cn(
                        'font-semibold text-sm',
                        formData.tipo === option.value
                          ? option.color
                          : 'text-gray-700 dark:text-gray-300'
                      )}>
                        {option.label}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">
                        {option.description}
                      </p>
                    </div>
                    {formData.tipo === option.value && (
                      <div className={cn(
                        'w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0',
                        option.bgColor
                      )}>
                        <div className={cn('w-2 h-2 rounded-full', option.color.replace('text-', 'bg-'))} />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Seleção de Abrangência */}
          {formData.tipo === 'NACIONAL' && (
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <HiGlobeAlt className="w-4 h-4 text-blue-500" />
                País <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.pais_id || ''}
                onChange={(e) => handlePaisChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                required
                disabled={loadingLists}
                aria-label="Seleção de país"
              >
                <option value="">Selecione um país</option>
                {paises.map(pais => (
                  <option key={pais.id} value={pais.id}>{pais.name}</option>
                ))}
              </select>
            </div>
          )}

          {formData.tipo === 'ESTADUAL' && (
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <HiMapPin className="w-4 h-4 text-blue-500" />
                Estado <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.estado_id || ''}
                onChange={(e) => handleEstadoChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                required
                disabled={loadingLists}
                aria-label="Seleção de estado"
              >
                <option value="">Selecione um estado</option>
                {estados.map(estado => (
                  <option key={estado.id} value={estado.id}>{estado.name}</option>
                ))}
              </select>
            </div>
          )}

          {(formData.tipo === 'MUNICIPAL' || formData.tipo === 'FOLGA') && (
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <HiMapPin className="w-4 h-4 text-blue-500" />
                Cidade <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.cidade_id || ''}
                onChange={(e) => handleCidadeChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                required
                disabled={loadingLists}
                aria-label="Seleção de cidade"
              >
                <option value="">Selecione uma cidade</option>
                {cidades.map(cidade => (
                  <option key={cidade.id} value={cidade.id}>{cidade.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Preview Card */}
          {/* {formData.data && formData.descricao && selectedTipoInfo && (
            <div className={cn(
              'p-3 rounded-lg border-2',
              selectedTipoInfo.borderColor,
              selectedTipoInfo.bgColor
            )}>
              <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                Pré-visualização
              </p>
              <div className="flex items-start gap-2">
                <HiCalendar className={cn('w-4 h-4 mt-0.5', selectedTipoInfo.color)} />
                <div className="flex-1 min-w-0">
                  <p className={cn('font-semibold text-sm truncate', selectedTipoInfo.color)}>
                    {formData.descricao}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    {format(new Date(formData.data + 'T12:00:00'), "dd/MM/yyyy", { locale: ptBR })} • {selectedTipoInfo.label}
                  </p>
                </div>
              </div>
            </div>
          )} */}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 pt-5 border-t border-gray-200 dark:border-neutral-700">
          {showDeleteConfirm ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <HiExclamationTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-red-900 dark:text-red-200">
                    Confirmar exclusão?
                  </p>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">
                    Esta ação não pode ser desfeita.
                  </p>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className={cn(
                        'px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-xs font-medium flex items-center gap-1.5',
                        isDeleting && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <HiTrash className="w-3.5 h-3.5" />
                      {isDeleting ? 'Excluindo...' : 'Sim, excluir'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                      className="px-3 py-1.5 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-xs font-medium"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              {/* Delete button (only in edit mode) */}
              {isEditMode && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg transition-colors text-sm font-medium"
                >
                  <HiTrash className="w-4 h-4" />
                  Excluir Feriado
                </button>
              )}

              {!isEditMode && <div />}

              {/* Save/Cancel buttons */}
              <div className="flex items-center gap-3 ml-auto">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className={cn(
                    'px-5 py-2.5 bg-white dark:bg-neutral-800 border border-gray-300 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors text-sm font-medium',
                    isSubmitting && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={cn(
                    'px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-semibold shadow-sm',
                    isSubmitting && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Salvando...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <HiCalendar className="w-4 h-4" />
                      {isEditMode ? 'Atualizar Feriado' : 'Criar Feriado'}
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </form>
    </BaseModal>
  );
};

